import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  login as authLogin,
  logout as authLogout,
  register as authRegister,
  authResendOtp,
  authResetPassword,
  authSendPasswordForgottenRequest,
  verifyOtp as authVerifyOtp
} from '../services/authService'; // Fonctions d'authentification de votre backend

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Contient les informations de l'utilisateur (id, email, role, etc.) de votre backend
  const [authToken, setAuthToken] = useState(null); // Le token JWT de votre backend
  const [refreshToken, setRefreshToken] = useState(null); // Le token de rafraîchissement de votre backend (si utilisé)
  const [expireAt, setExpireAt] = useState(null); // L'heure d'expiration du token, si disponible
  const [loading, setLoading] = useState(true); // Indique si l'authentification est en cours de chargement initial
  const [error, setError] = useState(null); // Gère les erreurs d'authentification

  useEffect(() => {
    const loadAuthFromLocalStorage = () => {
      try {
        // Charge l'état d'authentification de l'application depuis le stockage local (backend custom)
        const storedToken = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          try {
            setUser(JSON.parse(storedUser));
            setAuthToken(storedToken);
            setError(null);
          } catch (e) {
            console.error("Error parsing stored user data from local storage:", e);
            authLogout(); // Nettoie les données corrompues si elles ne sont pas valides
            setUser(null);
            setAuthToken(null);
          }
        } else {
          console.log("No application auth state found in local storage (custom backend).");
        }
      } catch (err) {
        console.error("Failed to load custom auth state from local storage:", err);
        setError(err.message);
      } finally {
        setLoading(false); // L'initialisation est terminée
      }
    };

    loadAuthFromLocalStorage();
  }, []); // S'exécute une seule fois au montage du composant

  const saveUserDataAfterLoginOrResetePassword = ({ token, user, refreshToken, expireAt }) => {
    setAuthToken(token);
    setUser(user); // Définit l'objet utilisateur de votre backend
    setRefreshToken(refreshToken); // Définit le token de rafraîchissement, si utilisé
    setExpireAt(expireAt); // Définit l'heure d'expiration du token,
    localStorage.setItem('authToken', token);
    localStorage.setItem('expireAt', expireAt);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('refreshToken', refreshToken); // Si vous utilisez un token de rafraîchissement
  }

  // Fonction de connexion (utilise votre backend)
  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authLogin(credentials); // Appel à votre service d'authentification backend
      const { token, user, refreshToken, expireAt } = response; // Supposons que la réponse contient token et user
      saveUserDataAfterLoginOrResetePassword({ token, user, refreshToken, expireAt });
      console.log("Login successful via custom backend, user:", user.email || user.id);
    } catch (error) {
      console.log(error)
      setError(error.message || "Login failed. Please check your credentials.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Fonction de déconnexion (utilise votre backend et nettoie le stockage local)
  const logout = () => {
    authLogout(); // Nettoie le stockage local (token et user de votre backend)
    setUser(null);
    setAuthToken(null);
    setRefreshToken(null);
    setExpireAt(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expireAt');
    localStorage.removeItem('deviceId'); // Nettoie également l'ID de l'appareil si nécessaire
    console.log("User logged out from custom backend.");
    setError(null);
  };

  const register = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authRegister(userData); // Appel à votre service d'authentification backend
      const { id, email } = response;
      console.log("Registration successful via custom backend, user:", email || id);
      return true;
    } catch (error) {
      console.log(error);
      setError(error.message || "Registration failed. Please check your details.");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (otpData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authVerifyOtp(otpData); // Appel à votre service d'authentification backend
      const { token, expireAt } = response;
      setAuthToken(token);
      setExpireAt(expireAt);
      localStorage.setItem('authToken', token);
    } catch (error) {
      setError(error.message)
      console.log(error)
      throw error
    }finally{
      setLoading(false);
    }
  }

  const resendOtp = async (phone) => {
    try {
      await authResendOtp(phone);
    } catch (error) {
      console.log(error)
      throw error
    }
  }

  const sendPasswordForgettenRequest = async (phone) => {
    setLoading(true);
    setError(null);
    try {
      await authSendPasswordForgottenRequest(phone);
    } catch (error) {
      console.log(error)
      setError(error.message)
      throw error
    } finally {
      setLoading(false);
    }
  }

  const resetPassword = async (passwordData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authResetPassword(passwordData); // Appel à votre service d'authentification backend
      const { token, user, refreshToken, expireAt } = response;
      saveUserDataAfterLoginOrResetePassword({ token, user, refreshToken, expireAt });
    } catch (error) {
      console.log(error)
      throw error
    }
  }

  const value = {
    user, // L'objet utilisateur de votre backend
    authToken, // Le token de votre backend
    refreshToken, // Le token de rafraîchissement de votre backend (si utilisé)
    expireAt, // L'heure d'expiration du token, si disponible
    loading,
    error,
    userId: user?.id, // L'ID de l'utilisateur, provient de votre backend
    login,
    logout,
    register,
    verifyOtp,
    resendOtp,
    sendPasswordForgettenRequest,
    resetPassword,
    isAuthenticated: !!user && !!authToken, // Vérifie si l'utilisateur de l'application est authentifié
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Affiche un spinner de chargement tant que l'authentification n'est pas prête */}
      {children}
    </AuthContext.Provider>
  );
};
